import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Grid,
  IconButton
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";
import { useTheme } from "@mui/material/styles";
import ErrorBoundary from "../../../components/ErrorBoundary";

export default function OnboardingStatusPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const theme = useTheme();
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSchools();
  }, []);

  useEffect(() => {
    if (selectedSchoolId) {
      loadOnboardingData(selectedSchoolId);
    }
  }, [selectedSchoolId]);

  const loadSchools = async () => {
    setLoading(true);
    try {
      const data = await fetchJson("/schools/onboarding", {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
      });
      // Fix: API returns { schools: [...] }, not an array directly
      const schoolsArray = Array.isArray(data.schools) ? data.schools : [];
      setSchools(schoolsArray);
      if (schoolsArray.length > 0) {
        setSelectedSchoolId(schoolsArray[0].id);
      }
    } catch (e) {
      setError(`Failed to load schools: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadOnboardingData = async (schoolId) => {
    try {
      const data = await fetchJson(`/onboarding/${schoolId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
      });
      setOnboardingData(data);
    } catch (e) {
      setError(`Failed to load onboarding data: ${e.message}`);
    }
  };

  const refreshData = () => {
    loadSchools();
    if (selectedSchoolId) {
      loadOnboardingData(selectedSchoolId);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': 
        return <CheckCircleIcon color="success" />;
      case 'in-progress': 
        return <CircularProgress size={20} />;
      case 'blocked': 
        return <ErrorIcon color="error" />;
      case 'pending': 
        return <InfoIcon color="info" />;
      default: 
        return <WarningIcon color="warning" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success.main';
      case 'in-progress': return 'info.main';
      case 'blocked': return 'error.main';
      case 'pending': return 'text.secondary';
      default: return 'warning.main';
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>
        {t("onboardingPage.title") || "Onboarding Status"}
      </Typography>
      
      {/* School Selection */}
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        gap: 2, 
        mb: 3,
        flexWrap: 'wrap'
      }}>
        <Select
          value={selectedSchoolId}
          onChange={(e) => setSelectedSchoolId(e.target.value)}
          displayEmpty
          sx={{ minWidth: 300 }}
          disabled={loading || schools.length === 0}
        >
          {loading ? (
            <MenuItem disabled>{t("onboardingPage.loading") || "Loading schools..."}</MenuItem>
          ) : schools.length === 0 ? (
            <MenuItem disabled>{t("onboardingPage.noSchools") || "No schools in onboarding"}</MenuItem>
          ) : (
            schools.map(school => (
              <MenuItem key={school.id} value={school.id}>
                {school.name} - {school.onboardingStatus}
              </MenuItem>
            ))
          )}
        </Select>
        
        <IconButton onClick={refreshData}>
          <RefreshIcon />
        </IconButton>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {onboardingData && (
        <>
          {/* Summary Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {t("onboardingPage.school") || "School"}
                  </Typography>
                  <Typography variant="h6">{onboardingData.school.name}</Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {t("onboardingPage.startDate") || "Start Date"}
                  </Typography>
                  <Typography variant="h6">
                    {new Date(onboardingData.startDate).toLocaleDateString()}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {t("onboardingPage.progress") || "Progress"}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <div style={{ 
                        height: 8, 
                        backgroundColor: theme.palette.grey[300],
                        borderRadius: 4
                      }}>
                        <div style={{
                          width: `${onboardingData.progress}%`,
                          height: '100%',
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: 4
                        }} />
                      </div>
                    </Box>
                    <Typography variant="body2">
                      {onboardingData.progress}%
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Onboarding Steps */}
          <Stepper orientation="vertical" activeStep={onboardingData.currentStep - 1}>
            {onboardingData.steps.map((step, index) => (
              <Step key={step.id} completed={step.status === 'completed'}>
                <StepLabel 
                  icon={getStatusIcon(step.status)}
                  error={step.status === 'blocked'}
                  sx={{ 
                    '& .MuiStepLabel-label': {
                      color: getStatusColor(step.status),
                      fontWeight: 'bold'
                    }
                  }}
                >
                  {step.name}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {step.description}
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption">
                        {t("onboardingPage.owner") || "Owner"}:
                      </Typography>
                      <Typography>{step.owner}</Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption">
                        {step.status === 'completed' 
                          ? t("onboardingPage.completedOn") || "Completed On" 
                          : t("onboardingPage.dueDate") || "Due Date"}:
                      </Typography>
                      <Typography>
                        {step.status === 'completed' 
                          ? new Date(step.completedAt).toLocaleDateString()
                          : new Date(step.dueDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {step.issues && step.issues.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("onboardingPage.issues") || "Issues"}:
                      </Typography>
                      <ul>
                        {step.issues.map((issue, i) => (
                          <li key={i}>{issue.description}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {}}
                    >
                      {t("onboardingPage.viewDetails") || "View Details"}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
          
          {/* History Log */}
          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("onboardingPage.history") || "Activity History"}
              </Typography>
              
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {onboardingData.history.map((entry, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      py: 1,
                      borderBottom: index < onboardingData.history.length - 1 
                        ? '1px solid #eee' 
                        : 'none'
                    }}
                  >
                    <Box sx={{ mr: 2 }}>
                      {getStatusIcon(entry.action)}
                    </Box>
                    <Box>
                      <Typography variant="body2">
                        {entry.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(entry.timestamp).toLocaleString()} • {entry.user}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
